// eslint-disable-next-line import/no-extraneous-dependencies
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { Icon, useMenuItems } from "@resource/atlas";
import { INSERT_TARGETS } from "@resource/atlas/content-editor/__utils/insert-options-targets-order";
import {
  createRichBlock,
  RichBlockProps,
} from "@resource/atlas/content-editor/__utils/rich-blocks";
import { atlasCalendarQuestion, atlasEdit2 } from "@resource/atlas/icons";
import { useLogEvent } from "analytics";
import { omit } from "lodash";
import { useMemo, useState } from "react";
import {
  DEFAULT_DATA,
  RequestAvailabilityData,
  RICH_BLOCK_NAME,
} from "shared/guide-content/rich-blocks/request-availability";
import { getCurrentIanaTimezone } from "shared/utils/timezones";

import { useRequestAvailability } from "./context";
import { RequestAvailabilityDisplay } from "./request-availability-display";
import { RequestAvailabilityModal } from "./request-availability-modal";

// component
// ---------

function RequestAvailabilityComponent({
  data,
  updateData,
  ConfigMenu,
}: RichBlockProps<RequestAvailabilityData>) {
  const [isCalendarVisible, setIsCalendarVisible] = useState(false);
  const { getAvailabilityRequestById, organization, candidate } =
    useRequestAvailability();
  const availabilityRequest = getAvailabilityRequestById(
    data.availabilityRequestId
  );
  const logEvent = useLogEvent({
    component: "RequestAvailabilityComponent",
    project: "Request Availability From A Candidate",
  });

  const selectionsBySender = useMemo(
    () =>
      availabilityRequest?.calendarEvents
        .filter((calendarEvent) => !!calendarEvent.createdBy)
        .map((calendarEvent) => ({
          ...calendarEvent,
          allDay: calendarEvent.allDay ?? false,
        })) ?? [],
    [availabilityRequest]
  );

  const menuItems = useMenuItems(
    (i) => [
      i.item({
        key: "suggest-times",
        leadingContent: <Icon content={atlasEdit2} />,
        children: "Edit",
        onClick: () => {
          setIsCalendarVisible(true);
          logEvent("Edit Availability Request Clicked");
        },
      }),
    ],
    [logEvent]
  );

  return (
    <>
      <ConfigMenu items={menuItems} />
      <RequestAvailabilityDisplay
        className="request-availability-block"
        isRecipient={false}
        title={data.title}
        description={data.description || ""}
        duration={data.duration}
        organization={organization ?? data.organization}
        candidate={candidate ?? data.candidate}
        isEditing
        onEdit={() => {
          setIsCalendarVisible(true);
        }}
        senderSuggestions={data.senderSuggestions}
        timezone={data.timezone || getCurrentIanaTimezone()}
      />
      <RequestAvailabilityModal
        isOpen={isCalendarVisible}
        onClose={() => setIsCalendarVisible(false)}
        // Suggestions for the module are going to be
        // interviewer availabilities fetched from their calendars
        data={{
          suggestions: data.senderSuggestions,
          meetingDescription: data.description,
          meetingTitle: data.title,
          meetingDuration: data.duration,
          timezone: data.timezone,
        }}
        // Selections for the module are going to be previous sender suggestions
        selections={
          selectionsBySender.length
            ? selectionsBySender
            : data.senderSuggestions
        }
        onConfirm={({
          suggestions,
          meetingTitle: title,
          meetingDescription: description,
          meetingDuration: duration,
          timezone,
        }) => {
          updateData((currentData) => ({
            ...currentData,
            title,
            description,
            duration,
            senderSuggestions: suggestions.map((calendarEvent) =>
              omit(calendarEvent, ["createdBy", "__typename"])
            ),
            edited: true,
            timezone,
          }));
          setIsCalendarVisible(false);
        }}
      />
    </>
  );
}

// block
// -----

export const {
  $createRequestAvailabilityNode,
  $isRequestAvailabilityNode,
  INSERT_REQUEST_AVAILABILITY_COMMAND,
  RequestAvailabilityNode,
  RequestAvailabilityPlugin,
  requestAvailabilityModule,
} = createRichBlock({
  name: RICH_BLOCK_NAME,
  defaultData: DEFAULT_DATA,
  RenderComponent: RequestAvailabilityComponent,
  useInsertOption(_, { INSERT_COMMAND }) {
    const [editor] = useLexicalComposerContext();

    return useMemo(
      () => ({
        label: "Request availability",
        icon: atlasCalendarQuestion,
        targets: {
          insert: INSERT_TARGETS.REQUEST_AVAILABILITY,
          fixedToolbar: { slot: "request-availability" },
        },
        onTrigger: () =>
          editor.dispatchCommand(INSERT_COMMAND, {
            ...DEFAULT_DATA,
            timezone: getCurrentIanaTimezone(),
          }),
      }),
      [editor, INSERT_COMMAND]
    );
  },
});
